import type { EditRequestFormData, UpdateRequestById } from "@nlc/shared-types";

import { editRequestFormSchema } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { z } from "zod";

import type { HttpError } from "@/lib/api/client";

import { toast } from "@/components/toast/toast-queue";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { prepareForApi } from "@/features/request/edit/utils/dataTransformer";
import apiClient from "@/lib/api/client";

// Field name mapper for more user-friendly toast messages
const fieldNameMap: Record<keyof EditRequestFormData, string> = {
  assets: "Files & Links",
  assignee: "Assignee",
  attachmentIds: "Attachments",
  brandId: "Brand",
  briefText: "Brief",
  collaboratorIds: "Collaborators",
  complexityId: "Complexity",
  creativeDirection: "Creative Direction",
  creatorId: "Created By",
  description: "Description",
  designerId: "Designer",
  designTypeId: "Design Type",
  dueDate: "Due Date",
  fileTypes: "File Types",
  inspirations: "Inspirations",
  internalDueDate: "Internal Due Date",
  internalNotes: "Internal Notes",
  lengthOrPlatform: "Length or platform",
  linkedRequestIds: "Linked Requests",
  magicalBriefCreator: "AI Brief Creator",
  musicGenre: "Music genre",
  name: "Request Name",
  preferredDesignerIds: "Preferred Designers",
  preferredFileType: "Preferred File Type",
  requestType: "Request Type",
  restrictAiAssets: "AI Assets",
  sizesNeeded: "Sizes Needed",
  statusId: "Status",
  styleOfVideo: "Style of Video",
  textForDesign: "Text for Design",
};

export function useFieldUpdate() {
  const { requestId } = useParams({ strict: false });
  const methods = useEditRequestFormContext();

  const timeoutRefs = useRef<Record<string, NodeJS.Timeout | null>>({});

  useEffect(
    () => () => {
      for (const timeout of Object.values(timeoutRefs.current)) {
        if (timeout) clearTimeout(timeout);
      }
    },
    [],
  );

  const { mutateAsync } = useMutation({
    mutationFn: async (data: Partial<UpdateRequestById>) =>
      apiClient.url(`/requests/edit/${requestId}`).patch(data).json(),
  });

  if (!requestId) {
    return {
      debouncedUpdateField: () =>
        // fieldName: keyof typeof editRequestFormSchema.shape,
        // value: unknown,
        {},
      updateField: () =>
        // fieldName: keyof typeof editRequestFormSchema.shape,
        // value: unknown,
        // onSuccess?: () => void,
        ({ success: true }),
    };
  }

  const updateField = async (
    fieldName: keyof typeof editRequestFormSchema.shape,
    value: unknown,
    onSuccess?: () => void,
  ) => {
    const displayName = fieldNameMap[fieldName] || fieldName;

    const originalValue = methods.getValues(fieldName);

    try {
      const validatedValue =
        await editRequestFormSchema.shape[fieldName].safeParseAsync(value);

      const dataForApi = prepareForApi({ [fieldName]: validatedValue.data });

      await mutateAsync(dataForApi);

      toast.add({
        description: `${displayName} has been successfully updated.`,
        timeout: 2000,
        title: `${displayName} updated`,
        type: "success",
      });

      if (onSuccess) onSuccess();

      return { success: true };
    } catch (error) {
      methods.setValue(fieldName, originalValue);

      const httpError = error as HttpError;

      let errorMessage = "An unknown error occurred.";

      if (httpError.message) {
        errorMessage = httpError.message;
      } else if (error instanceof z.ZodError) {
        errorMessage = error.errors[0]?.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.add({
        description: errorMessage,
        timeout: 2000,
        title: `${displayName} not updated`,
        type: "error",
      });

      return { error, success: false };
    }
  };

  const debouncedUpdateField = (
    fieldName: keyof typeof editRequestFormSchema.shape,
    value: unknown,
  ) => {
    if (timeoutRefs.current[fieldName as string]) {
      clearTimeout(timeoutRefs.current[fieldName as string]!);
    }

    timeoutRefs.current[fieldName as string] = setTimeout(() => {
      void updateField(fieldName, value);
      timeoutRefs.current[fieldName as string] = null;
    }, 1500);
  };

  return { debouncedUpdateField, updateField };
}
